// WHAT: React component for displaying and editing a single TimeTracQ aspect
// WHY:  Provides a dedicated, reusable UI card for managing an aspect's configuration (hours, status, color, flags)
// DOMAIN: Settings → TimeTracQ
// DEPENDENCIES: AspectConfig type
// RISKS:
//   - WARN: Changes are immediate in local state but not persisted until explicit save action in parent.
//   - NOTE: No internal validation of input values (e.g., hours range, color format).

import type { AspectConfig } from "../types";

/**
 * WHAT:
 *   Helper function to get CSS classes for aspect status badge.
 *
 * @param status - Aspect status (Include, Exclude, or other)
 *
 * @returns {string} CSS classes for the status badge
 *
 */
function getStatusBadgeClassName(status: string): string {
  if (status === "Include") {
    return "bg-green-900/50 text-green-300";
  }
  if (status === "Exclude") {
    return "bg-red-900/50 text-red-300";
  }
  return "bg-slate-600/50 text-slate-300";
}

/**
 * WHAT:
 *   Props interface for the `AspectCard` component.
 *
 * WHY:
 *   Defines the expected inputs for `AspectCard`, ensuring type safety and clarity
 *   regarding the data and callback functions it receives.
 */
interface AspectCardProperties {
  aspect: AspectConfig;
  aspectName: string;
  onToggleBoolean: (aspectName: string, field: "carryover" | "count_hours" | "count_plan") => void;
  onUpdateColor: (aspectName: string, color: string) => void;
  onUpdateHours: (aspectName: string, hours: string) => void;
  onUpdateStatus: (aspectName: string, status: string) => void;
  workspaceName: string;
}

/**
 * WHAT:
 *   React component that renders a card for a single TimeTracQ aspect.
 *
 * WHY:
 *   Encapsulates the UI and interaction logic for an individual aspect, making the
 *   `WorkspaceCard` component cleaner and improving reusability.
 *   It allows users to view and modify an aspect's hours, status, color, and boolean flags.
 *
 * PROPS:
 *   - workspaceName: string — The name of the parent workspace.
 *   - aspectName: string — The name of the aspect being displayed.
 *   - aspect: AspectConfig — The configuration object for the aspect.
 *   - onUpdateHours: (aspectName: string, hours: string) => void — Callback for updating aspect hours.
 *   - onUpdateStatus: (aspectName: string, status: string) => void — Callback for updating aspect status.
 *   - onUpdateColor: (aspectName: string, color: string) => void — Callback for updating aspect color.
 *   - onToggleBoolean: (aspectName: string, field: 'carryover' | 'count_hours' | 'count_plan') => void — Callback for toggling boolean flags.
 *
 * BEHAVIOR:
 *   - Displays aspect name, current hours, status, and color.
 *   - Provides input fields and controls for modifying aspect properties.
 *   - Uses visual indicators (badges) to show aspect status.
 *   - Triggers callback functions on user interaction to update the parent component's state.
 *
 * RISKS:
 *   - WARN: Changes made within this component are reflected in the parent's state immediately
 *           but are not persisted to the database until the parent's `saveConfig` function is called.
 *   - NOTE: The component relies on parent-provided callbacks for all state updates, ensuring a
 *           unidirectional data flow but requiring careful prop drilling.
 *
 * @param root0
 *
 * @param root0.workspaceName
 *
 * @param root0.aspectName
 *
 * @param root0.aspect
 *
 * @param root0.onUpdateHours
 *
 * @param root0.onUpdateStatus
 *
 * @param root0.onUpdateColor
 *
 * @param root0.onToggleBoolean
 *
 * @returns {JSX.Element} Aspect card component
 *
 */
export function AspectCard({
  aspect,
  aspectName,
  onToggleBoolean,
  onUpdateColor,
  onUpdateHours,
  onUpdateStatus,
  workspaceName: _unusedWorkspaceName,
}: Readonly<AspectCardProperties>) {
  // Reason: workspaceName is part of the props interface for consistency but not used in this component
  return (
    <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600/30 hover:border-slate-500/50 transition-all duration-200">
      {/* Aspect Header */}
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-md font-medium text-white">{aspectName}</h4>
        <div className="flex items-center space-x-2">
          <div
            className="w-4 h-4 rounded-full border-2 border-slate-500"
            style={{ backgroundColor: aspect.color }}
          />
          <span
            className={`text-xs px-2 py-1 rounded-full ${getStatusBadgeClassName(aspect.status)}`}
          >
            {aspect.status}
          </span>
        </div>
      </div>

      {/* Aspect Controls */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Hours */}
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wide">
            Stunden
          </label>
          <input
            className="w-full bg-slate-600/50 border border-slate-500/50 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
            min="0"
            onChange={(e) => {
              onUpdateHours(aspectName, e.target.value);
            }}
            step="0.5"
            type="number"
            value={aspect.hours}
          />
        </div>

        {/* Color */}
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wide">
            Farbe
          </label>
          <div className="flex items-center space-x-2">
            <input
              className="w-12 h-10 bg-slate-600/50 border border-slate-500/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 cursor-pointer"
              onChange={(e) => {
                onUpdateColor(aspectName, e.target.value);
              }}
              type="color"
              value={aspect.color}
            />
            <span className="text-xs text-slate-400">{aspect.color}</span>
          </div>
        </div>

        {/* Status */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wide">
            Status
          </label>
          <select
            className="w-full bg-slate-600/50 border border-slate-500/50 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
            onChange={(e) => {
              onUpdateStatus(aspectName, e.target.value);
            }}
            value={aspect.status}
          >
            <option value="Include">Include - Aktiv einplanen</option>
            <option value="Exclude">Exclude - Ausschließen</option>
            <option value="Inactive">Inactive - Deaktiviert</option>
          </select>
        </div>

        {/* Boolean Toggles */}
        <div className="sm:col-span-2">
          <div className="grid grid-cols-3 gap-3">
            <label className="flex items-center space-x-2 p-2 rounded-lg hover:bg-slate-600/30 transition-colors cursor-pointer">
              <input
                checked={aspect.carryover}
                className="rounded border-slate-500 bg-slate-600 text-blue-500 focus:ring-blue-500"
                onChange={() => {
                  onToggleBoolean(aspectName, "carryover");
                }}
                type="checkbox"
              />
              <span className="text-xs text-slate-300">Carryover</span>
            </label>

            <label className="flex items-center space-x-2 p-2 rounded-lg hover:bg-slate-600/30 transition-colors cursor-pointer">
              <input
                checked={aspect.count_hours}
                className="rounded border-slate-500 bg-slate-600 text-blue-500 focus:ring-blue-500"
                onChange={() => {
                  onToggleBoolean(aspectName, "count_hours");
                }}
                type="checkbox"
              />
              <span className="text-xs text-slate-300">Count Hours</span>
            </label>

            <label className="flex items-center space-x-2 p-2 rounded-lg hover:bg-slate-600/30 transition-colors cursor-pointer">
              <input
                checked={aspect.count_plan}
                className="rounded border-slate-500 bg-slate-600 text-blue-500 focus:ring-blue-500"
                onChange={() => {
                  onToggleBoolean(aspectName, "count_plan");
                }}
                type="checkbox"
              />
              <span className="text-xs text-slate-300">Count Plan</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
